import { useEffect, useRef } from 'react';
import { Leaf } from 'lucide-react';

// This route is the OAuth landing page when running inside the Capacitor native shell.
// Emergent Google Auth redirects here with #session_id=... after successful sign-in.
// The page immediately deep-links back into the native app using the com.emergent.farmworker:// URL scheme.
// The native app's App.addListener('appUrlOpen', ...) then catches this and exchanges the session.
//
// If the page is opened in a normal browser (not from the native app), we fall back to /dashboard
// so web users are not stranded here.

const APP_URL_SCHEME = 'com.emergent.farmworker://oauth';
const FALLBACK_TIMEOUT_MS = 2500;

const OAuthBridge = () => {
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || '';
    const params = new URLSearchParams(hash.replace(/^#/, ''));
    const sessionId = params.get('session_id');

    if (!sessionId) {
      window.location.replace('/dashboard');
      return;
    }

    // Try to open the native app via custom URL scheme.
    // If the native app is not installed (user opened this link in a normal browser),
    // fall back to routing straight to /dashboard#session_id=... so the web AuthCallback handles it.
    const nativeUrl = `${APP_URL_SCHEME}?session_id=${encodeURIComponent(sessionId)}`;
    window.location.href = nativeUrl;

    const fallback = setTimeout(() => {
      window.location.replace(`/dashboard#session_id=${encodeURIComponent(sessionId)}`);
    }, FALLBACK_TIMEOUT_MS);

    return () => clearTimeout(fallback);
  }, []);

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center bg-[#F4F4F0]"
      data-testid="oauth-bridge"
    >
      <div className="w-16 h-16 bg-[#2E4632] rounded-full flex items-center justify-center mb-4">
        <Leaf className="w-8 h-8 text-white" />
      </div>
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2E4632] mb-4"></div>
      <p className="text-[#5C5C5C] font-medium">Opening app...</p>
      <p className="text-xs text-[#5C5C5C]/70 mt-2 max-w-xs text-center px-4">
        If the app doesn&apos;t open automatically, please install FarmWorker from the App Store or
        Google Play.
      </p>
    </div>
  );
};

export default OAuthBridge;
